import { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client/react';
import {
  Mutation,
  MutationAssignToAccessGrantsArgs,
  MutationUnassignFromAccessGrantsArgs,
  Query,
  QueryGetVisitorGroupsForTenantArgs
} from 'graphql/generated/graphqlTypes';
import { ASSIGN_TO_ACCESS_GRANTS, UNASSIGN_TO_ACCESS_GRANTS } from 'graphql/mutations';
import { GET_VISITOR_GROUPS_FOR_TENANT_WITH_ACCESS_GRANTS } from 'graphql/queries';
import { filterGroupsForUser } from 'hooks/shared/filterGroupsForUser';
import { useEffect } from 'react';
import { setVisitorGroupsWithAccessGrantsAction, updateToAccessGrantsAction } from 'state/actions/accessGrants';
import { authVar, visitorsGroupsWithAccessGrantsVar } from 'state/vars';
import { ignoreHandledMutationError } from 'utils/Mutation/ignoreHandledMutationError';
import { BaseHookProps } from '../shared/types';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_VISITOR_GROUPS_FOR_TENANT_WITH_ACCESS_GRANTS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorGroupsForTenant'>,
  QueryGetVisitorGroupsForTenantArgs
> = GET_VISITOR_GROUPS_FOR_TENANT_WITH_ACCESS_GRANTS;
const ASSIGN_TO_ACCESS_GRANTS_TYPED: TypedDocumentNode<
  Pick<Mutation, 'assignToAccessGrants'>,
  MutationAssignToAccessGrantsArgs
> = ASSIGN_TO_ACCESS_GRANTS;
const UNASSIGN_TO_ACCESS_GRANTS_TYPED: TypedDocumentNode<
  Pick<Mutation, 'unassignFromAccessGrants'>,
  MutationUnassignFromAccessGrantsArgs
> = UNASSIGN_TO_ACCESS_GRANTS;

const useAccessGrants = ({ handleFetchError }: BaseHookProps) => {
  const isAdditionalLogsEnabled = process.env.REACT_APP_IS_ADDITIONAL_LOGS_ENABLED === 'true';
  const { user } = useReactiveVar(authVar);
  const visitorsGroupsWithAccessGrants = useReactiveVar(visitorsGroupsWithAccessGrantsVar);

  const [getVisitorsGroupsMembers, { loading }] = useLazyQuery(GET_VISITOR_GROUPS_FOR_TENANT_WITH_ACCESS_GRANTS_TYPED, {
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    const fetchVisitorsGroupsMembers = async () => {
      try {
        const { data } = await getVisitorsGroupsMembers();
        if (data) {
          const groups = filterGroupsForUser(data.getVisitorGroupsForTenant, user);
          if (isAdditionalLogsEnabled) console.log(groups);
          setVisitorGroupsWithAccessGrantsAction(groups);
        }
      } catch (err) {
        if (isAdditionalLogsEnabled) console.log(err);
        handleFetchError('Error while fetching visitor groups');
      }
    };
    void fetchVisitorsGroupsMembers();
  }, [getVisitorsGroupsMembers]);

  const [assignToAccessGrantsMutation, { loading: assignToAccessGrantsMutationLoading }] = useMutation(
    ASSIGN_TO_ACCESS_GRANTS_TYPED,
    {
      onCompleted: (data) => {
        data.assignToAccessGrants.forEach((grant) => updateToAccessGrantsAction(grant));
      },
      onError: () => {
        handleFetchError('Error while adding access');
      }
    }
  );

  const [unassignFromAccessGrantsMutation, { loading: unassignFromAccessGrantsMutationLoading }] = useMutation(
    UNASSIGN_TO_ACCESS_GRANTS_TYPED,
    {
      onCompleted: (data) => {
        data.unassignFromAccessGrants.forEach((grant) => updateToAccessGrantsAction(grant));
      },
      onError: () => {
        handleFetchError('Error while deleting access');
      }
    }
  );

  const assignToAccessGrants = async (accessGrantIds: string[], doorIds: string[], locationIds: string[]) => {
    // Failure is already handled in the mutation's onError, so the rejection is ignored here.
    const { data } = await ignoreHandledMutationError(
      assignToAccessGrantsMutation({
        variables: {
          accessGrantIds,
          doorIds,
          locationIds
        }
      })
    );
    return data;
  };

  const unassignFromAccessGrants = async (accessGrantIds: string[], doorIds: string[], locationIds: string[]) => {
    // Failure is already handled in the mutation's onError, so the rejection is ignored here.
    const { data } = await ignoreHandledMutationError(
      unassignFromAccessGrantsMutation({
        variables: {
          accessGrantIds,
          doorIds,
          locationIds
        }
      })
    );
    return data;
  };

  return {
    visitorsGroupsWithAccessGrants,
    loading,
    assignToAccessGrants,
    assignToAccessGrantsMutationLoading,
    unassignFromAccessGrants,
    unassignFromAccessGrantsMutationLoading
  };
};

export default useAccessGrants;
